import { useQuery } from '@tanstack/react-query'
import { MINUTE_MS } from 'data/constants'
import { env } from 'utils'
import { Address } from 'viem'

import { axiosClient } from '../api/apiClient'

// Types for the operators response
export interface OperatorMetrics {
    http20: number
    grpc: number
    grpc_start_time: string
    uptime_percentage: number
}

export interface StakableOperator {
    name: string
    image: string
    nodes: unknown[] // We can expand this type if needed
    commissionPercentage: number
    estimatedApr: number
    address: Address
    metrics: OperatorMetrics
    isActive: boolean
}

export interface StakeableOperatorsResponse {
    operators: StakableOperator[]
    networkEstimatedApy: number
}

export function useFetchOperatorsQuery() {
    return useQuery({
        queryKey: ['operators', 'yield'],
        queryFn: async (): Promise<StakeableOperatorsResponse> => {
            try {
                const response = await axiosClient.get(`${env.VITE_GATEWAY_URL}/operators/`, {
                    withCredentials: true,
                })
                return response.data
            } catch (err) {
                console.error('Error fetching operators yield:', err)
                throw err
            }
        },
        staleTime: MINUTE_MS / 2,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
    })
}

// Convenience hook to get yield for a specific operator
export function useOperatorYield(operatorAddress?: string) {
    const { data: operatorsData, isLoading, error } = useFetchOperatorsQuery()

    const operator = operatorsData?.operators.find(
        (op) => op.address.toLowerCase() === operatorAddress?.toLowerCase(),
    )

    return {
        operator,
        yield: operator?.estimatedApr,
        isLoading,
        error,
    }
}
